import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { automationsAPI } from '@/shared/lib/api';
import { ConfirmDialog } from '@travellegacy/nexops-design-system';
import { AutomationForm } from '@/features/automation/components/AutomationForm';
import { toast } from '@/shared/lib/toast';
import type { AutomationWithStats, Automation, AxiosErrorResponse } from '@/shared/types';

export function useAutomationModals() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Estados dos modais
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedAutomation, setSelectedAutomation] = useState<Automation | undefined>();

  const [confirmExecute, setConfirmExecute] = useState<{
    open: boolean;
    automation: AutomationWithStats | null;
  }>({
    open: false,
    automation: null,
  });

  const [confirmToggle, setConfirmToggle] = useState<{
    open: boolean;
    automation: AutomationWithStats | null;
  }>({
    open: false,
    automation: null,
  });

  const [confirmDelete, setConfirmDelete] = useState<{
    open: boolean;
    automation: AutomationWithStats | null;
    redirectOnSuccess?: boolean;
  }>({
    open: false,
    automation: null,
    redirectOnSuccess: false,
  });

  // Mutations
  const executeMutation = useMutation({
    mutationFn: (id: string) => automationsAPI.execute(id),
    onSuccess: () => {
      toast.success('Automação executada!', 'A execução foi iniciada.');
      queryClient.invalidateQueries({ queryKey: ['executions'] });
    },
    onError: (error: AxiosErrorResponse) => {
      toast.error('Erro ao executar', error.response?.data?.error?.message);
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (id: string) => automationsAPI.toggle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automations'] });
      queryClient.invalidateQueries({ queryKey: ['automation'] });
      toast.success('Status alterado', 'A automação foi atualizada.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => automationsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automations'] });
      toast.success('Automação deletada', 'A automação foi removida.');

      if (confirmDelete.redirectOnSuccess) {
        setTimeout(() => navigate('/automations'), 1000);
      }
    },
  });

  // Handlers
  const openExecuteModal = (automation: AutomationWithStats) => {
    setConfirmExecute({ open: true, automation });
  };

  const openToggleModal = (automation: AutomationWithStats) => {
    setConfirmToggle({ open: true, automation });
  };

  const openDeleteModal = (automation: AutomationWithStats, redirectOnSuccess = false) => {
    setConfirmDelete({ open: true, automation, redirectOnSuccess });
  };

  const openEditModal = (automation: Automation) => {
    setSelectedAutomation(automation);
    setIsEditOpen(true);
  };

  // Componentes dos modais
  const modals = (
    <>
      {/* Edit Modal */}
      <AutomationForm
        open={isEditOpen}
        onOpenChange={(open) => {
          setIsEditOpen(open);
          if (!open) setSelectedAutomation(undefined);
        }}
        automation={selectedAutomation}
      />

      {/* Execute Confirm */}
      <ConfirmDialog
        open={confirmExecute.open}
        onOpenChange={(open) => setConfirmExecute({ open, automation: null })}
        title="Executar Automação"
        description={`Deseja executar a automação "${confirmExecute.automation?.name}" agora? Uma nova execução será iniciada imediatamente.`}
        confirmText="Executar Agora"
        icon="execute"
        onConfirm={() => {
          if (confirmExecute.automation) {
            executeMutation.mutate(confirmExecute.automation.id);
          }
        }}
        isLoading={executeMutation.isPending}
      />

      {/* Toggle Confirm */}
      <ConfirmDialog
        open={confirmToggle.open}
        onOpenChange={(open) => setConfirmToggle({ open, automation: null })}
        title={confirmToggle.automation?.isActive ? 'Pausar Automação' : 'Ativar Automação'}
        description={
          confirmToggle.automation?.isActive
            ? `A automação "${confirmToggle.automation?.name}" será pausada e não executará automaticamente até ser reativada.`
            : `A automação "${confirmToggle.automation?.name}" será ativada e executará conforme o agendamento configurado.`
        }
        confirmText={confirmToggle.automation?.isActive ? 'Pausar' : 'Ativar'}
        icon="toggle"
        onConfirm={() => {
          if (confirmToggle.automation) {
            toggleMutation.mutate(confirmToggle.automation.id);
          }
        }}
        isLoading={toggleMutation.isPending}
      />

      {/* Delete Confirm */}
      <ConfirmDialog
        open={confirmDelete.open}
        onOpenChange={(open) =>
          setConfirmDelete({ open, automation: null, redirectOnSuccess: false })
        }
        title="Deletar Automação"
        description={`Tem certeza que deseja deletar a automação "${confirmDelete.automation?.name}"? Esta ação não pode ser desfeita.`}
        confirmText="Deletar"
        variant="destructive"
        icon="delete"
        onConfirm={() => {
          if (confirmDelete.automation) {
            deleteMutation.mutate(confirmDelete.automation.id);
          }
        }}
        isLoading={deleteMutation.isPending}
      />
    </>
  );

  return {
    modals,
    openExecuteModal,
    openToggleModal,
    openDeleteModal,
    openEditModal,
    isExecuting: executeMutation.isPending,
    isToggling: toggleMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
